/**
 * ImportedDataMappingTab
 * Admin tab for mapping CSV imported names to system users
 */

import React, { useState, useEffect, useMemo } from 'react';
import api from '../../../services/api';
import Spinner from '../../common/Spinner';
import Modal from '../../common/Modal';
import { toast } from 'react-toastify';

export default function ImportedDataMappingTab() {
  const [loading, setLoading] = useState(true);
  const [salesForms, setSalesForms] = useState([]);
  const [stats, setStats] = useState({ total: 0, fullyMapped: 0, partiallyMapped: 0, unmapped: 0 });
  const [unmappedNames, setUnmappedNames] = useState({ agents: [], leaders: [], managers: [], siteSupervisors: [], siteManagers: [], sids: [] });
  const [users, setUsers] = useState({ agents: [], leaders: [], managers: [], siteSupervisors: [], siteManagers: [], sids: [], asids: [] });
  const [groupedAgents, setGroupedAgents] = useState([]);

  // Bulk mapping modal
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkMappingData, setBulkMappingData] = useState({ csvName: '', fieldType: '', userId: '' });
  const [bulkLoading, setBulkLoading] = useState(false);

  // Single record mapping modal
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [recordMappingData, setRecordMappingData] = useState({
    agentId: '',
    salesLeaderId: '',
    managerId: '',
    siteSupervisorId: '',
    siteManagerId: '',
    sidId: ''
  });
  const [recordLoading, setRecordLoading] = useState(false);

  // Filter state
  const [filterStatus, setFilterStatus] = useState('all'); // all, unmapped, partial, mapped
  const [searchTerm, setSearchTerm] = useState('');

  // Build hierarchical grouping: Manager -> Leader -> Agents
  function buildGroupedAgents(allAgents, leaders, managers) {
    const groups = [];

    // Create lookup maps
    const leaderMap = {};
    leaders.forEach(l => {
      leaderMap[l.id] = { ...l, agents: [] };
    });

    const managerMap = {};
    managers.forEach(m => {
      managerMap[m.id] = { ...m, leaders: [] };
    });

    // Separate actual agents and leaders (since leaders are included in allAgents)
    const actualAgents = allAgents.filter(a => a.role === 'agent');
    const leaderUsers = allAgents.filter(a => a.role === 'leader');

    // Assign agents to their leaders
    actualAgents.forEach(agent => {
      if (agent.leaderId && leaderMap[agent.leaderId]) {
        leaderMap[agent.leaderId].agents.push(agent);
      }
    });

    // Assign leaders to their managers
    leaders.forEach(leader => {
      if (leader.managerId && managerMap[leader.managerId]) {
        managerMap[leader.managerId].leaders.push(leaderMap[leader.id]);
      }
    });

    // Build final structure
    managers.forEach(manager => {
      const managerData = managerMap[manager.id];
      if (managerData.leaders.length > 0) {
        groups.push({
          type: 'manager',
          manager: manager,
          leaders: managerData.leaders
        });
      }
    });

    // Add unassigned leaders (no manager)
    const unassignedLeaders = leaders.filter(l => !l.managerId || !managerMap[l.managerId]);
    if (unassignedLeaders.length > 0) {
      groups.push({
        type: 'unassigned',
        manager: { nickname: 'No Manager', fullName: 'No Manager' },
        leaders: unassignedLeaders.map(l => leaderMap[l.id])
      });
    }

    // Add standalone leaders (as selectable options)
    groups.push({
      type: 'leaders-standalone',
      leaders: leaderUsers
    });

    return groups;
  }

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      setLoading(true);
      const [importedRes, usersRes] = await Promise.all([
        api.get('/sales-forms/imported'),
        api.get('/sales-forms/imported/users')
      ]);

      setSalesForms(importedRes.data.salesForms);
      setStats(importedRes.data.stats);
      setUnmappedNames(importedRes.data.unmappedNames);
      setUsers(usersRes.data);

      // Build grouped agents structure (by manager -> leader -> agents)
      const { agents, leaders, managers } = usersRes.data;
      const grouped = buildGroupedAgents(agents, leaders, managers);
      setGroupedAgents(grouped);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load imported data');
    } finally {
      setLoading(false);
    }
  }

  // Filter sales forms based on status and search
  const filteredForms = useMemo(() => {
    return salesForms.filter(sf => {
      const meta = sf.importMetadata || {};
      const hasAgent = !!sf.agentId;
      const hasLeader = !!sf.salesLeaderId;
      const hasManager = !!sf.managerId;

      const needsAgent = meta.salesAgent && !hasAgent;
      const needsLeader = meta.teamLeader1 && !hasLeader;
      const needsManager = meta.manager && !hasManager;

      // Determine mapping status
      let mappingStatus;
      if (!needsAgent && !needsLeader && !needsManager) {
        mappingStatus = 'mapped';
      } else if (hasAgent || hasLeader || hasManager) {
        mappingStatus = 'partial';
      } else {
        mappingStatus = 'unmapped';
      }

      // Apply filter
      if (filterStatus !== 'all' && mappingStatus !== filterStatus) {
        return false;
      }

      // Apply search
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const clientMatch = sf.clientName?.toLowerCase().includes(search);
        const agentMatch = meta.salesAgent?.toLowerCase().includes(search);
        const leaderMatch = meta.teamLeader1?.toLowerCase().includes(search);
        const managerMatch = meta.manager?.toLowerCase().includes(search);
        return clientMatch || agentMatch || leaderMatch || managerMatch;
      }

      return true;
    });
  }, [salesForms, filterStatus, searchTerm]);

  // Open bulk mapping modal
  function openBulkMapping(csvName, fieldType) {
    setBulkMappingData({ csvName, fieldType, userId: '' });
    setShowBulkModal(true);
  }

  // Handle bulk mapping submit
  async function handleBulkMap() {
    if (!bulkMappingData.userId) {
      toast.error('Please select a user');
      return;
    }

    try {
      setBulkLoading(true);
      const res = await api.post('/sales-forms/imported/bulk-map', bulkMappingData);
      toast.success(`Mapped ${res.data.updatedCount} records`);
      setShowBulkModal(false);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Bulk mapping error:', error);
      toast.error(error.message || 'Bulk mapping failed');
    } finally {
      setBulkLoading(false);
    }
  }

  // Open single record mapping modal
  function openRecordMapping(record) {
    setSelectedRecord(record);
    const siteDb = record.designProject?.siteDatabase;
    setRecordMappingData({
      agentId: record.agentId || '',
      salesLeaderId: record.salesLeaderId || '',
      managerId: record.managerId || '',
      siteSupervisorId: siteDb?.siteSupervisorId || '',
      siteManagerId: siteDb?.siteManagerId || '',
      sidId: siteDb?.sidId || ''
    });
    setShowRecordModal(true);
  }

  // Handle single record mapping submit
  async function handleRecordMap() {
    try {
      setRecordLoading(true);
      await api.patch(`/sales-forms/imported/${selectedRecord.id}/map`, recordMappingData);
      toast.success('Mapping updated');
      setShowRecordModal(false);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Record mapping error:', error);
      toast.error(error.message || 'Mapping update failed');
    } finally {
      setRecordLoading(false);
    }
  }

  // Get user display name
  function getUserDisplayName(user) {
    if (!user) return '-';
    return user.nickname || user.fullName || user.id;
  }

  // Get mapping status badge
  function getMappingStatusBadge(sf) {
    const meta = sf.importMetadata || {};
    const hasAgent = !!sf.agentId;
    const hasLeader = !!sf.salesLeaderId;
    const hasManager = !!sf.managerId;

    const needsAgent = meta.salesAgent && !hasAgent;
    const needsLeader = meta.teamLeader1 && !hasLeader;
    const needsManager = meta.manager && !hasManager;

    if (!needsAgent && !needsLeader && !needsManager) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Mapped</span>;
    } else if (hasAgent || hasLeader || hasManager) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Partial</span>;
    } else {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Unmapped</span>;
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Records</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Fully Mapped</p>
          <p className="text-2xl font-bold text-green-600">{stats.fullyMapped}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Partially Mapped</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.partiallyMapped}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Unmapped</p>
          <p className="text-2xl font-bold text-red-600">{stats.unmapped}</p>
        </div>
      </div>

      {/* Unmapped Names - Quick Bulk Mapping */}
      {(unmappedNames.agents.length > 0 || unmappedNames.leaders.length > 0 || unmappedNames.managers.length > 0 || unmappedNames.siteSupervisors?.length > 0 || unmappedNames.siteManagers?.length > 0 || unmappedNames.sids?.length > 0) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Bulk Mapping</h3>
          <p className="text-sm text-gray-600 mb-4">Click on a name to map all records with that name to a user.</p>

          {unmappedNames.agents.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Unmapped Agents ({unmappedNames.agents.length})</h4>
              <div className="flex flex-wrap gap-2">
                {unmappedNames.agents.map(name => (
                  <button
                    key={name}
                    onClick={() => openBulkMapping(name, 'agent')}
                    className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {unmappedNames.leaders.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Unmapped Leaders ({unmappedNames.leaders.length})</h4>
              <div className="flex flex-wrap gap-2">
                {unmappedNames.leaders.map(name => (
                  <button
                    key={name}
                    onClick={() => openBulkMapping(name, 'leader')}
                    className="px-3 py-1 text-sm bg-purple-50 text-purple-700 rounded-full hover:bg-purple-100 transition-colors"
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {unmappedNames.managers.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Unmapped Managers ({unmappedNames.managers.length})</h4>
              <div className="flex flex-wrap gap-2">
                {unmappedNames.managers.map(name => (
                  <button
                    key={name}
                    onClick={() => openBulkMapping(name, 'manager')}
                    className="px-3 py-1 text-sm bg-green-50 text-green-700 rounded-full hover:bg-green-100 transition-colors"
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {unmappedNames.siteSupervisors?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Unmapped Site Supervisors ({unmappedNames.siteSupervisors.length})</h4>
              <div className="flex flex-wrap gap-2">
                {unmappedNames.siteSupervisors.map(name => (
                  <button
                    key={name}
                    onClick={() => openBulkMapping(name, 'siteSupervisor')}
                    className="px-3 py-1 text-sm bg-orange-50 text-orange-700 rounded-full hover:bg-orange-100 transition-colors"
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {unmappedNames.siteManagers?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Unmapped Site Managers ({unmappedNames.siteManagers.length})</h4>
              <div className="flex flex-wrap gap-2">
                {unmappedNames.siteManagers.map(name => (
                  <button
                    key={name}
                    onClick={() => openBulkMapping(name, 'siteManager')}
                    className="px-3 py-1 text-sm bg-teal-50 text-teal-700 rounded-full hover:bg-teal-100 transition-colors"
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {unmappedNames.sids?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Unmapped SIDs ({unmappedNames.sids.length})</h4>
              <div className="flex flex-wrap gap-2">
                {unmappedNames.sids.map(name => (
                  <button
                    key={name}
                    onClick={() => openBulkMapping(name, 'sid')}
                    className="px-3 py-1 text-sm bg-indigo-50 text-indigo-700 rounded-full hover:bg-indigo-100 transition-colors"
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by client name, agent, leader, or manager..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Records</option>
              <option value="unmapped">Unmapped Only</option>
              <option value="partial">Partially Mapped</option>
              <option value="mapped">Fully Mapped</option>
            </select>
          </div>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Records Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CSV Agent</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mapped Agent</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CSV Leader</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mapped Leader</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CSV Manager</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mapped Manager</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CSV SS</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mapped SS</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CSV SM</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mapped SM</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CSV SID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mapped SID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredForms.length === 0 ? (
                <tr>
                  <td colSpan={15} className="px-4 py-8 text-center text-gray-500">
                    No records found
                  </td>
                </tr>
              ) : (
                filteredForms.map(sf => {
                  const meta = sf.importMetadata || {};
                  return (
                    <tr key={sf.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getMappingStatusBadge(sf)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{sf.clientName}</div>
                        <div className="text-xs text-gray-500">RM {parseFloat(sf.estimatedPackagePrice || 0).toLocaleString()}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-sm ${!sf.agentId && meta.salesAgent ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                          {meta.salesAgent || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-green-600">
                          {getUserDisplayName(sf.agent)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-sm ${!sf.salesLeaderId && meta.teamLeader1 ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                          {meta.teamLeader1 || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-green-600">
                          {getUserDisplayName(sf.salesLeader)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-sm ${!sf.managerId && meta.manager ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                          {meta.manager || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-green-600">
                          {getUserDisplayName(sf.manager)}
                        </span>
                      </td>
                      {/* Site Supervisor columns */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-sm ${sf.designProject?.siteDatabase?.importMetadata?.siteSupervisor && !sf.designProject?.siteDatabase?.siteSupervisorId ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                          {sf.designProject?.siteDatabase?.importMetadata?.siteSupervisor || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-green-600">
                          {sf.designProject?.siteDatabase?.siteSupervisorName || '-'}
                        </span>
                      </td>
                      {/* Site Manager columns */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-sm ${sf.designProject?.siteDatabase?.importMetadata?.siteManager && !sf.designProject?.siteDatabase?.siteManagerId ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                          {sf.designProject?.siteDatabase?.importMetadata?.siteManager || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-green-600">
                          {sf.designProject?.siteDatabase?.siteManagerName || '-'}
                        </span>
                      </td>
                      {/* SID columns */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-sm ${sf.designProject?.siteDatabase?.importMetadata?.sid && !sf.designProject?.siteDatabase?.sidId ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                          {sf.designProject?.siteDatabase?.importMetadata?.sid || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-green-600">
                          {sf.designProject?.siteDatabase?.sid?.nickname || sf.designProject?.siteDatabase?.sid?.fullName || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={() => openRecordMapping(sf)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600">
            Showing {filteredForms.length} of {salesForms.length} records
          </p>
        </div>
      </div>

      {/* Bulk Mapping Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title={`Map "${bulkMappingData.csvName}"`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Select a {bulkMappingData.fieldType} to map all records with the name "{bulkMappingData.csvName}" to:
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select {
                bulkMappingData.fieldType === 'agent' ? 'Agent' :
                bulkMappingData.fieldType === 'leader' ? 'Leader' :
                bulkMappingData.fieldType === 'manager' ? 'Manager' :
                bulkMappingData.fieldType === 'siteSupervisor' ? 'Site Supervisor' :
                bulkMappingData.fieldType === 'siteManager' ? 'Site Manager' :
                'SID'
              }
            </label>
            <select
              value={bulkMappingData.userId}
              onChange={(e) => setBulkMappingData({ ...bulkMappingData, userId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a user...</option>
              {bulkMappingData.fieldType === 'agent' ? (
                <>
                  {groupedAgents.filter(g => g.type === 'manager' || g.type === 'unassigned').map((group, gIdx) => (
                    group.leaders.map((leader, lIdx) => (
                      <optgroup
                        key={`${gIdx}-${lIdx}`}
                        label={`${group.manager.nickname || group.manager.fullName} → ${leader.nickname || leader.fullName}`}
                      >
                        {leader.agents.map(agent => (
                          <option key={agent.id} value={agent.id}>
                            {agent.nickname || agent.fullName}
                          </option>
                        ))}
                      </optgroup>
                    ))
                  ))}
                  <optgroup label="── Leaders (as Sales Agent) ──">
                    {users.leaders.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.nickname || user.fullName}
                      </option>
                    ))}
                  </optgroup>
                </>
              ) : bulkMappingData.fieldType === 'leader' ? (
                users.leaders.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.nickname || user.fullName}
                  </option>
                ))
              ) : bulkMappingData.fieldType === 'manager' ? (
                users.managers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.nickname || user.fullName}
                  </option>
                ))
              ) : bulkMappingData.fieldType === 'siteSupervisor' ? (
                <>
                  {/* Show both Site Supervisors and Site Managers for SS assignment */}
                  {(users.siteSupervisors || []).length > 0 && (
                    <optgroup label="Site Supervisors">
                      {(users.siteSupervisors || []).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.nickname || user.fullName}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {(users.siteManagers || []).length > 0 && (
                    <optgroup label="Site Managers (as SS)">
                      {(users.siteManagers || []).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.nickname || user.fullName}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </>
              ) : bulkMappingData.fieldType === 'siteManager' ? (
                (users.siteManagers || []).map(user => (
                  <option key={user.id} value={user.id}>
                    {user.nickname || user.fullName}
                  </option>
                ))
              ) : (
                // SID dropdown - show both SID and ASID with optgroups
                <>
                  {(users.sids || []).length > 0 && (
                    <optgroup label="Senior Interior Designers (SID)">
                      {(users.sids || []).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.nickname || user.fullName}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {(users.asids || []).length > 0 && (
                    <optgroup label="Assistant SID (ASID)">
                      {(users.asids || []).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.nickname || user.fullName}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </>
              )}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => setShowBulkModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkMap}
              disabled={bulkLoading || !bulkMappingData.userId}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {bulkLoading ? 'Mapping...' : 'Apply to All'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Single Record Mapping Modal */}
      <Modal
        isOpen={showRecordModal}
        onClose={() => setShowRecordModal(false)}
        title="Edit Mapping"
      >
        {selectedRecord && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-gray-900">{selectedRecord.clientName}</p>
              <p className="text-xs text-gray-500">RM {parseFloat(selectedRecord.estimatedPackagePrice || 0).toLocaleString()}</p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {/* Agent Mapping */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agent {selectedRecord.importMetadata?.salesAgent && (
                    <span className="text-gray-500 font-normal">
                      (CSV: {selectedRecord.importMetadata.salesAgent})
                    </span>
                  )}
                </label>
                <select
                  value={recordMappingData.agentId}
                  onChange={(e) => setRecordMappingData({ ...recordMappingData, agentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No agent mapped</option>
                  {groupedAgents.filter(g => g.type === 'manager' || g.type === 'unassigned').map((group, gIdx) => (
                    group.leaders.map((leader, lIdx) => (
                      <optgroup
                        key={`${gIdx}-${lIdx}`}
                        label={`${group.manager.nickname || group.manager.fullName} → ${leader.nickname || leader.fullName}`}
                      >
                        {leader.agents.map(agent => (
                          <option key={agent.id} value={agent.id}>
                            {agent.nickname || agent.fullName}
                          </option>
                        ))}
                      </optgroup>
                    ))
                  ))}
                  <optgroup label="── Leaders (as Sales Agent) ──">
                    {users.leaders.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.nickname || user.fullName}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Leader Mapping */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sales Leader {selectedRecord.importMetadata?.teamLeader1 && (
                    <span className="text-gray-500 font-normal">
                      (CSV: {selectedRecord.importMetadata.teamLeader1})
                    </span>
                  )}
                </label>
                <select
                  value={recordMappingData.salesLeaderId}
                  onChange={(e) => setRecordMappingData({ ...recordMappingData, salesLeaderId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No leader mapped</option>
                  {users.leaders.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.nickname || user.fullName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Manager Mapping */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manager {selectedRecord.importMetadata?.manager && (
                    <span className="text-gray-500 font-normal">
                      (CSV: {selectedRecord.importMetadata.manager})
                    </span>
                  )}
                </label>
                <select
                  value={recordMappingData.managerId}
                  onChange={(e) => setRecordMappingData({ ...recordMappingData, managerId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No manager mapped</option>
                  {users.managers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.nickname || user.fullName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Site Supervisor Mapping */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site Supervisor {selectedRecord.designProject?.siteDatabase?.importMetadata?.siteSupervisor && (
                    <span className="text-gray-500 font-normal">
                      (CSV: {selectedRecord.designProject.siteDatabase.importMetadata.siteSupervisor})
                    </span>
                  )}
                </label>
                <select
                  value={recordMappingData.siteSupervisorId}
                  onChange={(e) => setRecordMappingData({ ...recordMappingData, siteSupervisorId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No site supervisor mapped</option>
                  {(users.siteSupervisors || []).length > 0 && (
                    <optgroup label="Site Supervisors">
                      {(users.siteSupervisors || []).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.nickname || user.fullName}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {(users.siteManagers || []).length > 0 && (
                    <optgroup label="Site Managers (as SS)">
                      {(users.siteManagers || []).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.nickname || user.fullName}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              {/* Site Manager Mapping */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site Manager {selectedRecord.designProject?.siteDatabase?.importMetadata?.siteManager && (
                    <span className="text-gray-500 font-normal">
                      (CSV: {selectedRecord.designProject.siteDatabase.importMetadata.siteManager})
                    </span>
                  )}
                </label>
                <select
                  value={recordMappingData.siteManagerId}
                  onChange={(e) => setRecordMappingData({ ...recordMappingData, siteManagerId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No site manager mapped</option>
                  {(users.siteManagers || []).map(user => (
                    <option key={user.id} value={user.id}>
                      {user.nickname || user.fullName}
                    </option>
                  ))}
                </select>
              </div>

              {/* SID Mapping */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SID {selectedRecord.designProject?.siteDatabase?.importMetadata?.sid && (
                    <span className="text-gray-500 font-normal">
                      (CSV: {selectedRecord.designProject.siteDatabase.importMetadata.sid})
                    </span>
                  )}
                </label>
                <select
                  value={recordMappingData.sidId}
                  onChange={(e) => setRecordMappingData({ ...recordMappingData, sidId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No SID mapped</option>
                  {(users.sids || []).length > 0 && (
                    <optgroup label="Senior Interior Designers (SID)">
                      {(users.sids || []).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.nickname || user.fullName}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {(users.asids || []).length > 0 && (
                    <optgroup label="Assistant SID (ASID)">
                      {(users.asids || []).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.nickname || user.fullName}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setShowRecordModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRecordMap}
                disabled={recordLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {recordLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
